import { useCallback, useEffect, useRef } from 'react';

import type { GroupSubscriptionData } from '@shared/dream-components';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { BulkCancelGroupSubscriptionInvitationsActionData } from '~/routes/__actions/bulk_cancel_group_subscription_invitations';
import type { BulkRemoveGroupSubscriptionSeatsActionData } from '~/routes/__actions/bulk_remove_group_subscription_seats';
import type { BulkResendGroupSubscriptionInvitationsActionData } from '~/routes/__actions/bulk_resend_group_subscription_invitations';
import type { CreateGroupSubscriptionInvitationsActionData } from '~/routes/__actions/create_group_subscription_invitations';
import type { ValidateGroupSubscriptionSeatsActionData } from '~/routes/__actions/validate_group_subscription_seats';
import type { GroupSubscriptionsLoaderData } from '~/routes/__loaders/group_subscriptions';
import type { GroupSubscriptionBulkSeatResult } from '~/services/api/subscription/bulkRemoveGroupSubscriptionSeats.server';
import type {
  CreateGroupSubscriptionInvitationsResponse,
  GroupSubscriptionInvitationResult,
} from '~/services/api/subscription/createGroupSubscriptionInvitations.server';
import type { GroupSubscriptionsPagination } from '~/services/api/subscription/getGroupSubscriptions.server';
import type { ValidateGroupSubscriptionSeatsResponse } from '~/services/api/subscription/validateGroupSubscriptionSeats.server';

interface Props {
  enabled: boolean;
}

// `payment_pending` is card auth (seconds); `pre_approved` is ACH/SEPA (days).
const FAST_POLL_MS = 5_000;
const SLOW_POLL_MS = 60_000;

/**
 * Both bulk-seat endpoints (remove + cancel-invite) return the same envelope:
 * a top-level `ok` for transport-layer success, plus a per-seat `results` array
 * so callers can surface partial failures. `results` is null on transport
 * failure (the request itself failed), non-null on any 2xx response.
 */
export type BulkSeatActionResult = {
  ok: boolean;
  error: string | null;
  results: GroupSubscriptionBulkSeatResult[] | null;
};

export type CreateInvitationsResult = {
  ok: boolean;
  error: string | null;
  /** Per-email envelope from Rails; non-null on 2xx even when some emails
   *  individually failed (partial success). */
  data: CreateGroupSubscriptionInvitationsResponse | null;
};

export type ValidateSeatsResult = {
  ok: boolean;
  error: string | null;
  data: ValidateGroupSubscriptionSeatsResponse | null;
};

interface ReturnProps {
  isLoading: boolean;
  data: GroupSubscriptionData[];
  pagination: GroupSubscriptionsPagination | null;
  error: string | null;
  refetch: () => void;
  /**
   * POST /bulk_removes. Handles 1..N seat IDs uniformly so single-row remove
   * shares the same partial-failure plumbing as multi-select. Use the per-seat
   * `results` array (not just top-level `ok`) to surface which seats failed.
   */
  bulkRemoveSeats: (groupSubscriptionId: string, seatIds: string[]) => Promise<BulkSeatActionResult>;
  /** POST /bulk_cancel_invites. Same envelope contract as bulkRemoveSeats. */
  bulkCancelInvites: (groupSubscriptionId: string, seatIds: string[]) => Promise<BulkSeatActionResult>;
  /**
   * POST /bulk_resend_invitations. Handles both `resend-invite` (invited seats)
   * and `reinvite` (removed/declined seats) — Rails' `ResendInvitation` branches
   * on seat status. Same envelope contract as the other bulk endpoints.
   */
  bulkResendInvitations: (groupSubscriptionId: string, seatIds: string[]) => Promise<BulkSeatActionResult>;
  /**
   * POST /invitations. On 2xx Rails returns the per-email `invitations` array
   * plus `dropped` (capped emails that didn't fit remaining_seats). Callers
   * should check `data.invitations[*].success` for partial failures, not just
   * the top-level `ok`.
   */
  createInvitations: (groupSubscriptionId: string, emails: string[]) => Promise<CreateInvitationsResult>;
  /**
   * POST /seats/validation. Preflight only — no writes server-side. Callers can
   * use the returned occupied/available/invalid_emails to surface fresh server
   * state before submitting an invite (e.g. another tab grabbed a seat).
   */
  validateSeats: (groupSubscriptionId: string, emails: string[]) => Promise<ValidateSeatsResult>;
}

const useGroupSubscriptions = ({ enabled }: Props): ReturnProps => {
  const fetcher = useNonRevalidatingFetcher<() => Promise<GroupSubscriptionsLoaderData>>();
  const bulkRemoveFetcher = useNonRevalidatingFetcher<() => Promise<BulkRemoveGroupSubscriptionSeatsActionData>>();
  const bulkCancelFetcher =
    useNonRevalidatingFetcher<() => Promise<BulkCancelGroupSubscriptionInvitationsActionData>>();
  const bulkResendFetcher =
    useNonRevalidatingFetcher<() => Promise<BulkResendGroupSubscriptionInvitationsActionData>>();
  const inviteFetcher = useNonRevalidatingFetcher<() => Promise<CreateGroupSubscriptionInvitationsActionData>>();
  const validateFetcher = useNonRevalidatingFetcher<() => Promise<ValidateGroupSubscriptionSeatsActionData>>();

  const fetchData = useCallback(() => {
    fetcher.load('/group_subscriptions');
  }, [fetcher]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: matches the useStaticOrders pattern — including fetcher in deps loops forever.
  useEffect(() => {
    if (enabled) {
      fetchData();
    }
  }, [enabled]);

  // Preserve the last successful response so a transient poll failure
  // (network blip, 5xx) doesn't blank the UI or — worse — collapse the
  // poll interval to null and permanently stop polling.
  const lastGoodRef = useRef<{
    groups: GroupSubscriptionData[];
    pagination: GroupSubscriptionsPagination | null;
  } | null>(null);
  if (fetcher.data?.group_subscriptions) {
    lastGoodRef.current = {
      groups: fetcher.data.group_subscriptions,
      pagination: fetcher.data.pagination ?? null,
    };
  }

  const groups = fetcher.data?.group_subscriptions ?? lastGoodRef.current?.groups ?? [];
  const pagination = fetcher.data?.pagination ?? lastGoodRef.current?.pagination ?? null;
  const getPollInterval = (): number | null => {
    // A seat addition awaiting its (off-session, near-instant) Stripe charge
    // fast-polls so the "payment pending" banner clears itself the moment the
    // invoice.payment_succeeded webhook bumps capacity + invites the queue.
    if (groups.some((g) => g.pending_seat_addition != null)) return FAST_POLL_MS;
    if (groups.some((g) => g.status === 'payment_pending')) return FAST_POLL_MS;
    if (groups.some((g) => g.status === 'pre_approved')) return SLOW_POLL_MS;
    return null;
  };
  const pollInterval = getPollInterval();

  useEffect(() => {
    if (!enabled || pollInterval == null) return;
    const id = setInterval(fetchData, pollInterval);
    return () => clearInterval(id);
  }, [enabled, pollInterval, fetchData]);

  // No refetch inside — the caller decides when to refetch (typically once
  // after the toast lands) so the loader's last-resolved snapshot reflects the
  // post-mutation state instead of an in-flight one.
  const bulkRemoveSeats = useCallback(
    async (groupSubscriptionId: string, seatIds: string[]): Promise<BulkSeatActionResult> => {
      const submitResult = await bulkRemoveFetcher.submit(
        JSON.stringify({ group_subscription_id: groupSubscriptionId, seat_ids: seatIds }),
        {
          method: 'post',
          action: '/bulk_remove_group_subscription_seats',
          encType: 'application/json',
        },
      );
      const body = submitResult.data;
      const results = body?.data?.results ?? null;
      const error = body?.error ?? (submitResult.ok ? null : 'Failed to remove seats');
      return { ok: submitResult.ok && !error, error, results };
    },
    [bulkRemoveFetcher],
  );

  const bulkCancelInvites = useCallback(
    async (groupSubscriptionId: string, seatIds: string[]): Promise<BulkSeatActionResult> => {
      const submitResult = await bulkCancelFetcher.submit(
        JSON.stringify({ group_subscription_id: groupSubscriptionId, seat_ids: seatIds }),
        {
          method: 'post',
          action: '/bulk_cancel_group_subscription_invitations',
          encType: 'application/json',
        },
      );
      const body = submitResult.data;
      const results = body?.data?.results ?? null;
      const error = body?.error ?? (submitResult.ok ? null : 'Failed to cancel invitations');
      return { ok: submitResult.ok && !error, error, results };
    },
    [bulkCancelFetcher],
  );

  const bulkResendInvitations = useCallback(
    async (groupSubscriptionId: string, seatIds: string[]): Promise<BulkSeatActionResult> => {
      const submitResult = await bulkResendFetcher.submit(
        JSON.stringify({ group_subscription_id: groupSubscriptionId, seat_ids: seatIds }),
        {
          method: 'post',
          action: '/bulk_resend_group_subscription_invitations',
          encType: 'application/json',
        },
      );
      const body = submitResult.data;
      const results = body?.data?.results ?? null;
      const error = body?.error ?? (submitResult.ok ? null : 'Failed to resend invitations');
      return { ok: submitResult.ok && !error, error, results };
    },
    [bulkResendFetcher],
  );

  const createInvitations = useCallback(
    async (groupSubscriptionId: string, emails: string[]): Promise<CreateInvitationsResult> => {
      const submitResult = await inviteFetcher.submit(
        JSON.stringify({ group_subscription_id: groupSubscriptionId, emails }),
        {
          method: 'post',
          action: '/create_group_subscription_invitations',
          encType: 'application/json',
        },
      );
      const body = submitResult.data;
      const data = body?.data ?? null;
      const error = body?.error ?? (submitResult.ok ? null : 'Failed to send invitations');
      // ANY successful response means seats may have been created (even if some
      // emails failed individually). Refetching keeps counts + the seats list
      // honest for the next render of the modal/card.
      if (submitResult.ok && data) {
        const anyCreated = data.invitations.some((i: GroupSubscriptionInvitationResult) => i.success);
        if (anyCreated) fetchData();
      }
      return { ok: submitResult.ok && !error, error, data };
    },
    [inviteFetcher, fetchData],
  );

  const validateSeats = useCallback(
    async (groupSubscriptionId: string, emails: string[]): Promise<ValidateSeatsResult> => {
      const submitResult = await validateFetcher.submit(
        JSON.stringify({ group_subscription_id: groupSubscriptionId, emails }),
        {
          method: 'post',
          action: '/validate_group_subscription_seats',
          encType: 'application/json',
        },
      );
      const body = submitResult.data;
      const data = body?.data ?? null;
      const error = body?.error ?? (submitResult.ok ? null : 'Failed to validate seats');
      return { ok: submitResult.ok && !error, error, data };
    },
    [validateFetcher],
  );

  // Only true on the initial load. Polling refetches keep the previous data
  // visible (via lastGoodRef) so a transient failure doesn't bounce the UI
  // back into a loading state.
  const isLoading = lastGoodRef.current === null && enabled;

  return {
    refetch: fetchData,
    isLoading,
    data: groups,
    pagination,
    error: fetcher.data?.error ?? null,
    bulkRemoveSeats,
    bulkCancelInvites,
    bulkResendInvitations,
    createInvitations,
    validateSeats,
  };
};

export default useGroupSubscriptions;
